import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiClient, setActiveBrandKey } from '../api/client';
import { resolveConsoleBrand, type AccessibleBrand, type MeResponse } from '../lib/brand';
import { useAuth } from './AuthContext';
import { Button, Callout, Spinner } from '../components/ui';

interface BrandContextValue {
  /** The brand this console acts as - every admin-api call is scoped to it. */
  brand: AccessibleBrand;
  /** Every brand this admin may act as on this backend (includes `brand`). */
  brands: AccessibleBrand[];
  role: string;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

type State =
  | { status: 'loading' }
  | { status: 'ready'; value: BrandContextValue }
  | { status: 'no-access'; brands: AccessibleBrand[] }
  | { status: 'error'; message: string };

/**
 * Resolves the console's brand from the hostname (see lib/brand.ts) once the
 * admin is signed in, pins it on the API client as X-Brand-Key, and blocks
 * the app until that's done so no page ever fetches under the wrong brand.
 * An admin with no access to this hostname's brand sees a clear stop with
 * links to the consoles they can use instead of an empty, 403-riddled UI.
 */
export function BrandProvider({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setActiveBrandKey(null);
    apiClient<MeResponse>('/me')
      .then((me) => {
        if (cancelled) return;
        const brand = resolveConsoleBrand(window.location.hostname, me);
        if (!brand) {
          setState({ status: 'no-access', brands: me.brands });
          return;
        }
        setActiveBrandKey(brand.brandKey);
        document.title = `${brand.name} Admin`;
        setState({ status: 'ready', value: { brand, brands: me.brands, role: me.role } });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ status: 'error', message: error instanceof Error ? error.message : 'Unable to load your admin profile' });
      });
    return () => {
      cancelled = true;
    };
    // Re-resolve on a different sign-in; the hostname can't change without a reload.
  }, [user?.username]);

  if (state.status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="mx-auto max-w-lg p-6">
        <Callout variant="error" title="Could not load your admin profile">
          {state.message}
        </Callout>
      </div>
    );
  }

  if (state.status === 'no-access') {
    return (
      <div className="mx-auto max-w-lg space-y-4 p-6">
        <Callout variant="warning" title={`You don't have access to the ${window.location.hostname} console`}>
          {state.brands.length > 0 ? (
            <>
              <p>Your account can manage:</p>
              <ul className="mt-2 list-disc pl-5">
                {state.brands.map((b) => (
                  <li key={b.brandKey}>
                    {/* ponytail: production console hostname convention; dev hosts edit the URL */}
                    <a className="underline" href={`${window.location.protocol}//admin.${b.primaryDomain}`}>
                      {b.name}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>Your account hasn't been granted any brand on this backend. Ask a super-admin.</p>
          )}
        </Callout>
        <Button variant="outline" onClick={() => signOut()}>
          Sign out
        </Button>
      </div>
    );
  }

  return <BrandContext.Provider value={state.value}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandContextValue {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}
